"use client"

import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { AnimatedMouse } from "./animated-mouse"
import { CheckCircle2, LayoutDashboard, PenTool, BarChart3, Clock, Trophy, Target, PlayCircle, FileText } from "lucide-react"

const MODULES = [
  { id: 1, title: "Information and Ideas", status: "mastered", progress: 100, topics: "4/4 topics mastered", action: "Review" },
  { id: 2, title: "Craft and Structure", status: "in-progress", progress: 65, topics: "5/8 topics mastered", action: "Continue" },
  { id: 3, title: "Expression of Ideas", status: "in-progress", progress: 30, topics: "2/6 topics mastered", action: "Continue" },
  { id: 4, title: "Standard English Conventions", status: "not-started", progress: 0, topics: "0/10 topics mastered", action: "Continue" }
]

const EXAMS = [
  { id: 1, title: "Practice Test 1", date: "Available Now", questions: 98, time: "2h 14m", isCompleted: true, score: 1420 },
  { id: 2, title: "Practice Test 2", date: "Available Now", questions: 98, time: "2h 14m", isCompleted: false },
  { id: 3, title: "Practice Test 3", date: "Available Now", questions: 98, time: "2h 14m", isCompleted: false },
  { id: 4, title: "Practice Test 4", date: "Locked", questions: 98, time: "2h 14m", isLocked: true }
]

export function AnimatedMockDashboard() {
  const containerRef = useRef<HTMLDivElement>(null)
  const elementsRef = useRef<{ [key: string]: HTMLElement | null }>({})

  const [mousePos, setMousePos] = useState({ x: 200, y: 300 })
  const [isClicking, setIsClicking] = useState(false)
  const [hoveredElement, setHoveredElement] = useState<string | null>(null)

  const [activeTab, setActiveTab] = useState("Practice")
  const [practiceView, setPracticeView] = useState<"modules" | "question">("modules")
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)

  // Handle manual tab click
  const handleTabClick = (tab: string) => {
    setIsAutoPlaying(false)
    setActiveTab(tab)
    if (tab === "Practice") setPracticeView("modules")
  }

  // Function to move mouse accurately to a tracked element
  const moveToTarget = async (id: string, waitFn: (ms: number) => Promise<unknown>, durationMs: number = 800) => {
    setHoveredElement(id)
    if (elementsRef.current[id] && containerRef.current) {
      const el = elementsRef.current[id]!
      const container = containerRef.current
      const elRect = el.getBoundingClientRect()
      const currentRect = container.getBoundingClientRect()

      // Calculate center of element relative to the container
      const x = elRect.left - currentRect.left + (elRect.width / 2)
      const y = elRect.top - currentRect.top + (elRect.height / 2)

      setMousePos({ x, y })
    }
    await waitFn(durationMs)
  }

  // Animation sequence
  useEffect(() => {
    if (!isAutoPlaying) return;
    let isSubscribed = true;

    const sequence = async () => {
      const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

      while (isSubscribed && isAutoPlaying) {
        // Start position
        setMousePos({ x: 250, y: 350 })
        setHoveredElement(null)
        setActiveTab("Practice")
        setPracticeView("modules")
        await wait(1000)

        if (!isSubscribed || !isAutoPlaying) break;

        // Move to first module (just hovering somewhere)
        await moveToTarget('mod-1', wait, 1400)

        if (!isSubscribed || !isAutoPlaying) break;

        // Move to second module's continue button
        await moveToTarget('mod-2-btn', wait, 900)

        if (!isSubscribed || !isAutoPlaying) break;

        // Click second module
        setIsClicking(true)
        await wait(150)
        setIsClicking(false)
        setPracticeView("question")
        await wait(1500)

        // Read question, move around a bit randomly
        setMousePos({ x: 200, y: 200 })
        await wait(1000)
        setMousePos({ x: 400, y: 250 })
        await wait(1000)

        if (!isSubscribed || !isAutoPlaying) break;

        // Move to option B
        await moveToTarget('opt-B', wait, 800)

        // Click option B
        setIsClicking(true)
        await wait(150)
        setIsClicking(false)
        await wait(2000)

        if (!isSubscribed || !isAutoPlaying) break;

        // Reset and show modules again
        setPracticeView("modules")
        await wait(1000)
      }
    }

    sequence()
    return () => { isSubscribed = false }
  }, [isAutoPlaying])

  const renderDashboard = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
      className="flex flex-col h-full space-y-4"
    >
      <div className="flex justify-between items-end">
        <div>
          <h3 className="font-semibold text-lg">Performance Overview</h3>
          <span className="text-xs text-muted-foreground">Last 30 Days</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-indigo-50/50 dark:bg-indigo-900/10 p-4 rounded-xl border border-indigo-100 dark:border-indigo-800/30">
          <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 mb-2">
            <Trophy className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Est. Score</span>
          </div>
          <div className="text-3xl font-bold">1480 <span className="text-sm font-normal text-muted-foreground">/ 1600</span></div>
          <div className="text-xs text-emerald-600 font-medium mt-1">+40 points from last test</div>
        </div>

        <div className="bg-zinc-50 dark:bg-zinc-900/40 p-4 rounded-xl border border-zinc-100 dark:border-zinc-800">
          <div className="flex items-center gap-2 text-zinc-500 mb-2">
            <Target className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Accuracy</span>
          </div>
          <div className="text-3xl font-bold">84%</div>
          <div className="w-full bg-zinc-200 dark:bg-zinc-800 h-1.5 mt-2 rounded-full overflow-hidden">
            <div className="bg-zinc-900 dark:bg-zinc-100 h-full w-[84%]" />
          </div>
        </div>
      </div>

      <div className="flex-1 bg-zinc-50 dark:bg-zinc-900/40 rounded-xl border border-zinc-100 dark:border-zinc-800 p-4 flex flex-col">
        <h4 className="text-sm font-semibold mb-3">Topic Strengths</h4>
        <div className="space-y-4 flex-1 justify-center flex flex-col">
          {[
            { label: 'Algebra', val: 92 },
            { label: 'Advanced Math', val: 78 },
            { label: 'Information & Ideas', val: 88 },
            { label: 'Expression of Ideas', val: 65 },
          ].map(skill => (
            <div key={skill.label}>
              <div className="flex justify-between text-xs font-medium mb-1">
                <span>{skill.label}</span>
                <span>{skill.val}%</span>
              </div>
              <div className="w-full bg-zinc-200 dark:bg-zinc-800 h-1.5 rounded-full overflow-hidden">
                <div className="bg-indigo-500 h-full rounded-full" style={{ width: `${skill.val}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  )

  const renderTakeExam = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
      className="flex flex-col h-full"
    >
      <div className="flex justify-between items-end mb-4">
        <div>
          <h3 className="font-semibold text-lg">Mock Exams</h3>
          <span className="text-xs text-muted-foreground">Full-length adaptive tests</span>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto pr-1 custom-scrollbar">
        {EXAMS.map(exam => (
          <div key={exam.id} className={`p-4 rounded-xl border ${exam.isLocked ? 'opacity-60 bg-zinc-50 dark:bg-zinc-900/20' : 'bg-white dark:bg-zinc-950'} border-zinc-200 dark:border-zinc-800 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center`}>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold">{exam.title}</h4>
                {exam.isCompleted && <span className="text-[10px] uppercase font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 px-2 py-0.5 rounded text-xs">Completed</span>}
              </div>
              <div className="flex items-center gap-3 text-xs font-medium text-muted-foreground">
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {exam.time}</span>
                <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {exam.questions} Qs</span>
              </div>
            </div>

            {exam.isCompleted ? (
              <div className="text-right">
                <div className="text-sm font-bold text-indigo-600 dark:text-indigo-400">Score: {exam.score}</div>
                <button className="text-xs text-muted-foreground hover:text-foreground underline mt-0.5">View Results</button>
              </div>
            ) : exam.isLocked ? (
              <button className="text-xs px-4 py-2 rounded-md font-medium bg-zinc-100 text-zinc-400 dark:bg-zinc-900 cursor-not-allowed">
                Locked
              </button>
            ) : (
              <button className="text-xs px-4 py-2 rounded-md font-medium bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-white dark:text-zinc-900">
                Start Test
              </button>
            )}
          </div>
        ))}
      </div>
    </motion.div>
  )

  const renderPracticeQuestion = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
      className="flex flex-col h-full bg-white dark:bg-zinc-950 absolute inset-0 z-10"
    >
      {/* Question Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-zinc-100 dark:border-zinc-800/50">
        <span className="text-[10px] font-bold tracking-wider text-muted-foreground uppercase">Module 1: Reading and Writing</span>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-xs font-mono font-medium">28:45</span>
        </div>
      </div>

      {/* Question Layout */}
      <div className="flex-1 flex flex-col sm:flex-row overflow-hidden">
        {/* Passage / Notes side */}
        <div className="sm:w-1/2 p-6 border-b sm:border-b-0 sm:border-r border-zinc-100 dark:border-zinc-800/50 overflow-y-auto">
          <div className="prose prose-sm dark:prose-invert">
            <p className="font-semibold text-sm mb-2">Notes:</p>
            <ul className="text-sm space-y-2 mb-4 list-disc pl-4 text-zinc-700 dark:text-zinc-300">
              <li>The Atlantic Charter (1941) by Roosevelt & Churchill set a vision for the postwar world.</li>
              <li>A key point was the right of self-determination for all people.</li>
            </ul>
            <p className="text-sm font-medium mt-6 text-foreground">
              The student wants to emphasize the rights-based goal. Which choice uses the notes effectively?
            </p>
          </div>
        </div>

        {/* Choices side */}
        <div className="sm:w-1/2 p-6 flex flex-col bg-zinc-50/30 dark:bg-transparent overflow-y-auto">
          <p className="text-xs font-semibold mb-4 text-foreground/80">Select an answer:</p>
          <div className="flex-1 space-y-3">
            {[
              { l: 'A', t: "The Atlantic Charter, released by Roosevelt and Churchill in 1941, set out a vision for the postwar world." },
              { l: 'B', t: "A key point of the Atlantic Charter was the right of self-determination for all people." },
              { l: 'C', t: "The Atlantic Charter called for global economic cooperation and was released on August 14, 1941." },
              { l: 'D', t: "Released in 1941, the Atlantic Charter was a joint declaration by the U.S. President and British Prime Minister." }
            ].map(opt => (
              <div
                ref={(el) => { elementsRef.current[`opt-${opt.l}`] = el }}
                key={opt.l}
                className={`p-3 rounded-lg border flex gap-3 text-sm transition-colors cursor-pointer ${hoveredElement === `opt-${opt.l}`
                  ? 'border-indigo-400 bg-indigo-50/50 dark:bg-indigo-900/20'
                  : 'border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950'
                  }`}
              >
                <div className="shrink-0 w-6 h-6 rounded-full border border-zinc-300 dark:border-zinc-700 flex items-center justify-center text-[10px] font-bold text-muted-foreground mt-0.5">
                  {opt.l}
                </div>
                <div className="text-zinc-600 dark:text-zinc-300 leading-snug">{opt.t}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 flex justify-end">
            <button className="px-6 py-2 rounded-md bg-indigo-200 text-indigo-400 dark:bg-indigo-900 dark:text-indigo-500 font-medium text-sm cursor-not-allowed">
              Check Answer
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  )

  const renderPracticeModules = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
      className="flex flex-col h-full"
    >
      <div className="flex justify-between items-end mb-4 px-1">
        <h3 className="font-semibold text-lg">Study Modules</h3>
        <span className="text-xs font-medium text-muted-foreground">Reading & Writing</span>
      </div>

      <div className="flex-1 space-y-3 relative overflow-hidden px-1">
        {MODULES.map((mod, i) => (
          <div
            ref={(el) => { elementsRef.current[`mod-${mod.id}`] = el }}
            key={mod.id}
            className={`p-3 rounded-xl border flex items-center gap-4 transition-all duration-300 ${hoveredElement === `mod-${mod.id}` || hoveredElement === `mod-${mod.id}-btn`
              ? 'border-indigo-300 dark:border-indigo-700 shadow-sm bg-indigo-50/30 dark:bg-indigo-500/5'
              : 'border-zinc-100 dark:border-zinc-800 bg-white dark:bg-zinc-950'
              }`}
          >
            {/* Icon / Status */}
            {/* -600/-700 rather than -500: the percentage is 12px bold text, so it
                owes 4.5:1. indigo-500 on indigo-50 measured 4.08:1 and blue-500 on
                blue-50 is no better; zinc-400 on zinc-50 was far below. */}
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${mod.status === 'mastered' ? 'bg-blue-50 text-blue-700' : mod.status === 'in-progress' ? 'bg-indigo-50 text-indigo-700' : 'bg-zinc-50 text-zinc-600'}`}>
              {mod.status === 'mastered' ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                <span className="text-xs font-bold">{mod.progress}%</span>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm text-foreground truncate">{mod.title}</h4>
              <p className="text-[11px] text-muted-foreground">{mod.topics}</p>
            </div>

            {/* Progress & Action */}
            <div className="flex items-center gap-3">
              <div className="w-20 h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden hidden sm:block">
                <div
                  className={`h-full rounded-full ${mod.status === 'mastered' ? 'bg-blue-500' : 'bg-indigo-500'}`}
                  style={{ width: `${mod.progress}%` }}
                />
              </div>

              <button
                ref={(el) => { elementsRef.current[`mod-${mod.id}-btn`] = el; }}
                className={`text-xs px-4 py-2 rounded-md font-medium transition-colors ${hoveredElement === `mod-${mod.id}-btn` ? 'bg-indigo-600 text-white' :
                  mod.status === 'mastered'
                    ? 'border border-zinc-200 text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300'
                    : 'bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-white dark:text-zinc-900'
                  }`}>
                {mod.action}
              </button>
            </div>
          </div>
        ))}

        <div className="absolute -bottom-2 left-0 right-0 h-12 bg-gradient-to-t from-white dark:from-zinc-950 to-transparent pointer-events-none" />
      </div>
    </motion.div>
  )

  return (
    <div ref={containerRef} className="relative w-full max-w-[1200px] mx-auto transform scale-[0.60] sm:scale-[0.70] md:scale-[0.75] lg:scale-[0.80] xl:scale-[0.85] origin-center sm:origin-right lg:origin-center bg-white dark:bg-zinc-950 rounded-[2rem] shadow-2xl overflow-hidden border border-zinc-200/50 dark:border-zinc-800/50 flex flex-col h-[600px]">
      {/* Header bar */}
      <div className="h-16 border-b border-border/40 flex items-center px-4 justify-between bg-background/95 backdrop-blur z-20">
        <div className="flex items-center hover:opacity-90 transition-opacity">
          <div className="relative h-6 w-24 sm:h-7 sm:w-28">
            <img
              src="/satbuddy-logo.png"
              alt="SATBuddy"
              className="object-contain object-left h-full w-full"
            />
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-2 text-sm font-medium">
          {["Dashboard", "Practice", "Take Exam"].map((tab) => {
            const isActive = activeTab === tab;
            let btnClass = "px-4 py-2 rounded-md flex items-center gap-1.5 transition-colors font-medium text-sm ";

            if (tab === "Take Exam") {
              btnClass += "bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 dark:bg-indigo-600 dark:text-white dark:hover:bg-indigo-700";
            } else if (isActive) {
              btnClass += "text-primary bg-zinc-100 dark:bg-zinc-800/50";
            } else {
              btnClass += "text-muted-foreground hover:text-primary hover:bg-zinc-50 dark:hover:bg-zinc-800/30";
            }

            return (
              <button
                key={tab}
                className={btnClass}
                onClick={() => handleTabClick(tab)}
              >
                {tab === "Dashboard" && <LayoutDashboard className="w-4 h-4" />}
                {tab === "Practice" && <PenTool className="w-4 h-4" />}
                {tab}
              </button>
            )
          })}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 p-5 overflow-hidden flex flex-col relative bg-white dark:bg-zinc-950">
        <AnimatePresence mode="wait" initial={false}>
          {activeTab === "Dashboard" && <div key="dash" className="h-full">{renderDashboard()}</div>}
          {activeTab === "Take Exam" && <div key="exam" className="h-full">{renderTakeExam()}</div>}
          {activeTab === "Practice" && practiceView === "modules" && <div key="modules" className="h-full">{renderPracticeModules()}</div>}
          {activeTab === "Practice" && practiceView === "question" && <div key="question" className="h-full">{renderPracticeQuestion()}</div>}
        </AnimatePresence>
      </div>

      {/* Absolute animated mouse - only show when auto playing */}
      <AnimatePresence>
        {isAutoPlaying && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 pointer-events-none"
          >
            <AnimatedMouse x={mousePos.x} y={mousePos.y} isClicking={isClicking} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
